/**
 * What the RANGER did this tick that the quiet rules care about (03 §2: "a gunshot or
 * explosion"). `explosion`, `rocket` and `throw` events do not say whose they are, and the
 * enemy throws grenades too, so blasts are attributed: the ranger's grenades and bombs are
 * remembered from one tick to the next, and a blast where one of them has just vanished is his.
 */

import type { Ctx } from '../ctx';
import type { World } from '../types';
import { dist } from './common';

export interface PlayerNoise {
  /** A gunshot, a LAW launch or an explosion of his making: breaks quiet rules, answers the signal. */
  loud: boolean;
  /** He fired or threw something: a stolen uniform does not survive it (02 §6.4). */
  fired: boolean;
}

/** Events carry the ranger's position at the moment of firing; he cannot have gone far since. */
const AT_RANGER = 1;
const AT_ORDNANCE = 0.75;

export function readPlayerNoise(ctx: Ctx): PlayerNoise {
  const w = ctx.world;
  const r = w.ranger;
  const noise: PlayerNoise = { loud: false, fired: false };
  for (const ev of ctx.events) {
    if (ev.type === 'shot') {
      // The suppressed pistol is still a shot fired - the uniform does not survive it - but it breaks no silence.
      if (ev.fromPlayer) {
        noise.fired = true;
        if (ev.suppressed !== true) noise.loud = true;
      }
    } else if (ev.type === 'rocket') {
      if (dist(ev.x, ev.z, r.x, r.z) <= AT_RANGER) noise.loud = noise.fired = true;
    } else if (ev.type === 'throw') {
      if (dist(ev.x, ev.z, r.x, r.z) <= AT_RANGER) noise.fired = true;
    } else if (ev.type === 'explosion') {
      for (const item of w.objective.ordnance) {
        const gone = !w.thrown.some((g) => g.id === item.id) && !w.bombs.some((b) => b.id === item.id);
        if (gone && dist(ev.x, ev.z, item.x, item.z) <= AT_ORDNANCE) noise.loud = true;
      }
    }
  }
  return noise;
}

/** Note where the ranger's live grenades will land and where his bombs lie, for next tick's attribution. */
export function rememberOrdnance(w: World): void {
  const list: Array<{ id: number; x: number; z: number }> = [];
  // Smoke makes no bang: only his grenades are remembered.
  for (const g of w.thrown) if (g.fromPlayer && g.kind === 'grenade') list.push({ id: g.id, x: g.endX, z: g.endZ });
  for (const b of w.bombs) list.push({ id: b.id, x: b.x, z: b.z });
  w.objective.ordnance = list;
}
